import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, FormGroup, ControlLabel } from 'react-bootstrap';
import Utils from '../../util/Utils.jsx';

const NarrativeQueriesTab = ({ dispatch }) => {
	const [queries, setQueries] = useState([]);
	const [selected, setSelected] = useState('');
	const [version, setVersion] = useState(1);
	const [sql, setSql] = useState('');
	const [notes, setNotes] = useState('');
	const [datasetId, setDatasetId] = useState('');
	const [priorId, setPriorId] = useState('');
	const [compare, setCompare] = useState(null);
	const [message, setMessage] = useState('');
	const [busy, setBusy] = useState(false);

	const load = useCallback(async () => {
		const result = await Utils.ajaxAsync('narrativeQueries', {}, dispatch, { suppressErrorAlert: true });
		if (!result || result.success === false) {
			setMessage((result && result.error) || 'Unable to load the query library.');
			return;
		}
		setQueries(result.queries || []);
	}, [dispatch]);

	useEffect(() => {
		load();
	}, [load]);

	const current = queries.find(query => query.key === selected) || null;
	const chosen = current && current.versions.find(row => Number(row.version) === Number(version));

	const choose = (key) => {
		const query = queries.find(row => row.key === key);
		setSelected(key);
		setCompare(null);
		if (!query) return;
		const enabled = query.versions.find(row => row.enabled) || query.versions[query.versions.length - 1];
		setVersion(enabled ? enabled.version : 1);
		setSql(enabled ? enabled.sql : '');
	};

	const save = async () => {
		setBusy(true);
		setMessage('');
		try {
			const result = await Utils.ajaxAsync('narrativeQuerySave', {
				key: selected,
				sql,
				notes
			}, dispatch, { suppressErrorAlert: true });
			if (!result || result.success === false) {
				setMessage((result && result.error) || 'The new version was not saved.');
				return;
			}
			setMessage(`Saved ${selected} version ${result.version}. It is not enabled yet.`);
			setNotes('');
			await load();
		} finally {
			setBusy(false);
		}
	};

	const enable = async (nextVersion) => {
		setBusy(true);
		setMessage('');
		try {
			const result = await Utils.ajaxAsync('narrativeQueryEnable', {
				key: selected,
				version: nextVersion
			}, dispatch, { suppressErrorAlert: true });
			if (!result || result.success === false) {
				setMessage((result && result.error) || 'That version was not enabled.');
				return;
			}
			setMessage(`Enabled ${selected}@${nextVersion}.`);
			await load();
		} finally {
			setBusy(false);
		}
	};

	const testRun = async () => {
		setBusy(true);
		setMessage('');
		try {
			const result = await Utils.ajaxAsync('narrativeQueryTest', {
				key: selected,
				version,
				dataset: datasetId,
				priorDataset: priorId
			}, dispatch, { suppressErrorAlert: true });
			if (!result || result.success === false) {
				setMessage((result && result.error) || 'The test run failed.');
				return;
			}
			setCompare(result);
		} finally {
			setBusy(false);
		}
	};

	const downloadTraining = async () => {
		setBusy(true);
		setMessage('');
		try {
			const result = await Utils.ajaxAsync('narrativeExport', {}, dispatch, { suppressErrorAlert: true });
			if (!result || result.success === false) {
				setMessage((result && result.error) || 'The export failed.');
				return;
			}
			const blob = new Blob([result.body || ''], { type: 'application/jsonl' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = result.filename || 'narrative-training.jsonl';
			link.click();
			URL.revokeObjectURL(url);
			setMessage(`Exported ${result.bytes || 0} bytes. Nothing was uploaded.`);
		} finally {
			setBusy(false);
		}
	};

	return (
		<section className="superAdminAi">
			<h2>Narrative queries</h2>
			<p>
				Each schedule email is fed by these queries. Saving writes a new version.
				The model cannot edit SQL. Flag counts come from narrative reviews.
			</p>
			<Button bsSize="small" disabled={busy} onClick={downloadTraining}>Download training JSONL</Button>
			{message ? <p className="superAdminAiMeta">{message}</p> : null}
			<table className="table table-condensed">
				<thead>
					<tr>
						<th>Query</th>
						<th>Section</th>
						<th>Enabled</th>
						<th>Flags</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					{queries.map(query => (
						<tr key={query.key}>
							<td>{query.key}</td>
							<td>{query.section}</td>
							<td>@{query.enabledVersion}</td>
							<td>{query.flagCount}</td>
							<td><Button bsSize="xsmall" onClick={() => choose(query.key)}>Open</Button></td>
						</tr>
					))}
				</tbody>
			</table>
			{current ? (
				<div>
					<h3>{current.title}</h3>
					<FormGroup>
						<ControlLabel>Version</ControlLabel>
						<FormControl
							componentClass="select"
							value={version}
							onChange={e => {
								const next = Number(e.target.value);
								setVersion(next);
								const row = current.versions.find(item => Number(item.version) === next);
								setSql(row ? row.sql : '');
							}}>
							{current.versions.map(row => (
								<option key={row.version} value={row.version}>
									{row.version}{row.enabled ? ' enabled' : ''} — {row.postProcess}
								</option>
							))}
						</FormControl>
					</FormGroup>
					{chosen ? <p className="superAdminAiMeta">{chosen.notes}</p> : null}
					<FormGroup>
						<ControlLabel>SQL for a new version</ControlLabel>
						<FormControl componentClass="textarea" rows={10} value={sql} onChange={e => setSql(e.target.value)} />
					</FormGroup>
					<FormGroup>
						<ControlLabel>Notes</ControlLabel>
						<FormControl componentClass="textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
					</FormGroup>
					<Button bsStyle="primary" bsSize="small" disabled={busy || !sql.trim()} onClick={save}>Save as new version</Button>
					{' '}
					<Button bsSize="small" disabled={busy} onClick={() => enable(version)}>Enable or roll back to this version</Button>
					<FormGroup>
						<ControlLabel>Test dataset</ControlLabel>
						<FormControl type="number" value={datasetId} placeholder="Current dataset id" onChange={e => setDatasetId(e.target.value)} />
					</FormGroup>
					<FormGroup>
						<ControlLabel>Prior dataset</ControlLabel>
						<FormControl type="number" value={priorId} placeholder="Prior dataset id" onChange={e => setPriorId(e.target.value)} />
					</FormGroup>
					<Button bsSize="small" disabled={busy} onClick={testRun}>Test run side by side</Button>
					{compare ? (
						<div>
							<p className="superAdminAiMeta">Post-process: {compare.postProcess}</p>
							<pre className="aiInsightsDebug">{`Enabled @${compare.current.version}\n${compare.current.sql}\n${compare.current.error || ''}\n${JSON.stringify(compare.current.rows && compare.current.rows.rows, null, 2)}\n\nCandidate @${compare.candidate.version}\n${compare.candidate.sql}\n${compare.candidate.error || ''}\n${JSON.stringify(compare.candidate.rows && compare.candidate.rows.rows, null, 2)}`}</pre>
							{compare.facts ? <pre className="aiInsightsDebug">{JSON.stringify(compare.facts, null, 2)}</pre> : null}
						</div>
					) : null}
				</div>
			) : null}
		</section>
	);
};

NarrativeQueriesTab.propTypes = {
	dispatch: PropTypes.func.isRequired
};

export default NarrativeQueriesTab;
